import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { getActiveCampaigns } from "@/data/campaigns";

const AUTOPLAY_MS = 6000;

export default function CampaignCarousel() {
  const campaigns = getActiveCampaigns();
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selected, setSelected] = useState(0);
  const [hovering, setHovering] = useState(false);
  const interacted = useRef(false);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    const onPointerDown = () => { interacted.current = true; };
    emblaApi.on("select", onSelect);
    emblaApi.on("pointerDown", onPointerDown);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("pointerDown", onPointerDown);
    };
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const id = setInterval(() => {
      if (!hovering && !interacted.current) emblaApi.scrollNext();
    }, AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [emblaApi, hovering]);

  if (campaigns.length === 0) return null;

  return (
    <section className="w-full" data-testid="campaign-carousel">
      <div
        className="relative overflow-hidden group"
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
      >
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex">
            {campaigns.map((c) => (
              <Link
                key={c.id}
                to="/kampanyalar"
                className="relative flex-[0_0_100%] min-w-0 block"
                data-testid={`campaign-slide-${c.id}`}
              >
                <picture>
                  <source media="(max-width: 767px)" srcSet={c.gorselMobil || c.gorselMasaustu} />
                  <img
                    src={c.gorselMasaustu}
                    alt={c.baslik || "VoyaCar Rental kampanyası"}
                    className="w-full h-full object-cover aspect-square md:aspect-[16/6]"
                    loading="lazy"
                  />
                </picture>
              </Link>
            ))}
          </div>
        </div>

        {campaigns.length > 1 && (
          <>
            <button
              onClick={() => { interacted.current = true; emblaApi?.scrollPrev(); }}
              data-testid="campaign-prev-button"
              aria-label="Önceki kampanya"
              className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 h-11 w-11 items-center justify-center rounded-full bg-white/90 hover:bg-white text-[#171717] shadow-md transition-colors"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={() => { interacted.current = true; emblaApi?.scrollNext(); }}
              data-testid="campaign-next-button"
              aria-label="Sonraki kampanya"
              className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 h-11 w-11 items-center justify-center rounded-full bg-white/90 hover:bg-white text-[#171717] shadow-md transition-colors"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </>
        )}
      </div>

      {campaigns.length > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          {campaigns.map((c, i) => (
            <button
              key={c.id}
              onClick={() => { interacted.current = true; emblaApi?.scrollTo(i); }}
              data-testid={`campaign-dot-${i}`}
              aria-label={`Kampanya ${i + 1}`}
              className={`h-2 rounded-full transition-all ${i === selected ? "w-6 bg-[#A11016]" : "w-2 bg-neutral-300 hover:bg-neutral-400"}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}
